import * as React from 'react';
import {
  List,
  Datagrid,
  DateField,
  TextField,
  ListProps,
  TextInput,
  useRecordContext,
  useListContext,
  FieldProps,
  BulkDeleteButton,
} from 'react-admin';
import { Tooltip, Typography } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';

import { Event } from '../domain';
import { UuidField } from '../components/fields';
import { EventPagination } from '../components/pagination';

export const EventsIcon = EventIcon;

const shortenUuid = (value: string) => value.slice(0, 8);

// Clickable field that sets a filter when clicked
const ClickableNameField: React.FC<FieldProps> = ({ source = '' }) => {
  const record = useRecordContext<Event>();
  const { filterValues, setFilters } = useListContext();
  
  if (!record) return null;
  
  const value = record[source as keyof Event];
  if (!value) return <TextField source={source} />;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newFilters = { ...filterValues, [source]: value };
    setFilters(newFilters, null);
  };
  
  return (
    <span
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        textDecoration: 'underline',
        color: '#1976d2',
      }}
    >
      {String(value)}
    </span>
  );
};

// Clickable UUID field that sets a filter when clicked
const ClickableUuidField: React.FC<FieldProps & { label?: string }> = ({ 
  source = '', 
  label 
}) => {
  const record = useRecordContext<Event>();
  const { filterValues, setFilters } = useListContext();
  
  if (!record) return null;
  
  const value = record[source as keyof Event];
  if (!value) return <UuidField source={source} label={label} />;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newFilters = { ...filterValues, [source]: value };
    setFilters(newFilters, null);
  };
  
  return (
    <Tooltip title={String(value)}>
      <Typography
        variant="body2"
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          color: '#1976d2',
          display: 'inline-block',
        }}
      >
        {shortenUuid(String(value))}
      </Typography>
    </Tooltip>
  );
};

const postFilters = [
  <TextInput
    key="name"
    label="Event Name"
    source="name"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="participant_id"
    label="Participant ID"
    source="participant_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="session_id"
    label="Session ID"
    source="session_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="show_id"
    label="Show ID"
    source="show_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
];

const EventBulkActionButtons = () => (
  <>
    <BulkDeleteButton
      icon={<DeleteIcon />}
      confirmTitle="Delete Events"
      confirmContent="Are you sure you want to permanently delete the selected events? This action cannot be undone."
      mutationMode="pessimistic"
    />
  </>
);

export const EventList: React.FC<ListProps> = (props) => (
  <List
    {...props}
    perPage={50}
    pagination={<EventPagination />}
    exporter={false}
    sort={{ field: 'occurred_at', order: 'DESC' }}
    filters={postFilters}
  >
    <Datagrid 
      expand={<EventExpandDetails />}
      bulkActionButtons={<EventBulkActionButtons />}
    >
      <DateField source="occurred_at" showTime={true} locales={['en-GB']} />
      <ClickableNameField source="name" />
      <ClickableUuidField source="participant_id" label="Participant" />
      <ClickableUuidField source="session_id" label="Session" />
      <DateField source="created_at" showTime={true} locales={['en-GB']} />
    </Datagrid>
  </List>
);

const EventExpandDetails: React.FC = () => {
  const record = useRecordContext<Event>();
  if (!record) return null;

  return (
    <div style={{ padding: '20px' }}>
      <TextField source="id" />
      <TextField source="name" />
      <UuidField source="participant_id" label="Participant ID" />
      <UuidField source="session_id" label="Session ID" />
      <DateField source="occurred_at" showTime={true} locales={['en-GB']} />
      <DateField source="created_at" showTime={true} locales={['en-GB']} />
      <EventPayloadField source="payload" label="Payload" />
    </div>
  );
};

const EventPayloadField: React.FC<{ source: string; label: string }> = ({
  source,
}) => {
  const record = useRecordContext<Event>();
  if (!record) return null;

  const payload = record[source as keyof Event];
  const payloadString =
    typeof payload === 'object' ? JSON.stringify(payload, null, 2) : String(payload);

  return (
    <div style={{ marginTop: '10px' }}>
      <pre
        style={{
          backgroundColor: '#f5f5f5',
          padding: '10px',
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '400px',
          fontSize: '12px',
          fontFamily: 'monospace',
        }}
      >
        {payloadString}
      </pre>
    </div>
  );
};

